import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Heart, Activity, Moon, Pill, Phone, Mail, FileText, AlertCircle, Check, Clock, User, MessageSquare, Upload, Loader, Pencil } from 'lucide-react'
import { mdApi } from '../api/mdApi'
import { getRiskColor } from '../config/theme'
import ConversationsSection from '../components/ConversationsSection'
import ConcernsPanel from '../components/ConcernsPanel'
import MedicalHistoryTabs from '../components/MedicalHistoryTabs'
import FileCard from '../components/FileCard'
import InlineEditField, { phoneValidator, emailValidator, formatPhoneDisplay } from '../components/InlineEditField'
import CareTeamEditModal from '../components/CareTeamEditModal'
import VaccinationUploadDialog from '../components/VaccinationUploadDialog'
import CaregiversSection from '../components/CaregiversSection'
import LunaTextPopup from '../components/LunaPopup/LunaTextPopup'
import './PatientDetail.css'

function PatientDetail() {
  const { patientId } = useParams()
  const navigate = useNavigate()
  const [patient, setPatient] = useState(null)
  const [wearableData, setWearableData] = useState(null)
  const [loading, setLoading] = useState(true)
  
  // Discharge notes state
  const [dischargeNotes, setDischargeNotes] = useState('')
  const [existingNotes, setExistingNotes] = useState(null)
  const [savingNotes, setSavingNotes] = useState(false)
  const [notesSuccess, setNotesSuccess] = useState(false)
  const [notesError, setNotesError] = useState(null)
  
  // Outbound call state
  const [callingPatient, setCallingPatient] = useState(false)
  const [callSuccess, setCallSuccess] = useState(null)
  const [callError, setCallError] = useState(null)
  
  // Nurse callback queue state
  const [nurseCallbacks, setNurseCallbacks] = useState([])
  const [loadingCallbacks, setLoadingCallbacks] = useState(false)
  
  // Discharge files state
  const [dischargeFiles, setDischargeFiles] = useState([])
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const fileInputRef = useRef(null)
  
  // Patient editing state
  const [showCareTeamModal, setShowCareTeamModal] = useState(false)
  
  // Vaccination upload state
  const [showVaccinationDialog, setShowVaccinationDialog] = useState(false)
  const [deletingVaccination, setDeletingVaccination] = useState(false)
  
  // Luna chat state
  const [activeLunaChat, setActiveLunaChat] = useState(null)

  const handleDiscussWithLuna = (concern) => {
    setActiveLunaChat({
      patientId: patientId,
      patientName: `${patient?.first_name || ''} ${patient?.last_name || ''}`,
      activeConcern: concern.description || concern.concern_type,
      concernId: concern.concern_id || concern.id,
    })
  }

  // Open Luna from the floating button with general patient context (no concern)
  const openGeneralLuna = () => {
    setActiveLunaChat({
      patientId: patientId,
      patientName: `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim(),
      activeConcern: null,
      concernId: null,
    })
  }
  

  useEffect(() => {
    loadPatientData()
  }, [patientId])

  const loadPatientData = async () => {
    try {
      setLoading(true)
      const [patientData, wearableInfo] = await Promise.all([
        mdApi.getPatientDetail(patientId),
        mdApi.getWearableSummary(patientId)
      ])
      
      setPatient(patientData)
      setWearableData(wearableInfo)
      
      // Load discharge notes if they exist
      try {
        const notesData = await mdApi.getDischargeNotes(patientId)
        if (notesData.discharge_notes) {
          setExistingNotes(notesData.discharge_notes)
          setDischargeNotes(notesData.discharge_notes.notes || '')
        }
      } catch (error) {
        // Notes may not exist yet, that's okay
        console.log('No existing discharge notes')
      }
      
      // Load nurse callbacks for this patient
      loadNurseCallbacks()
      
      // Load discharge files for this patient
      loadDischargeFiles()
    } catch (error) {
      console.error('Error loading patient data:', error)
    } finally {
      setLoading(false)
    }
  }
  
  const loadDischargeFiles = async () => {
    try {
      setLoadingFiles(true)
      const response = await mdApi.getDischargeFiles(patientId)
      // Backend returns { status, patient_id, count, files: [...] }
      setDischargeFiles(response.files || [])
    } catch (error) {
      console.error('Error loading discharge files:', error)
      // Files may not exist yet, that's okay
      setDischargeFiles([])
    } finally {
      setLoadingFiles(false)
    }
  }
  
  const handleFileUpload = async (event) => {
    const files = event.target.files
    if (!files || files.length === 0) return
    
    try {
      setUploadingFile(true)
      setUploadError(null)
      
      // Upload each file
      for (const file of files) {
        await mdApi.uploadDischargeFile(patientId, file)
      }
      
      // Reload the file list
      await loadDischargeFiles()
      
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      setUploadError(error.message || 'Failed to upload file')
    } finally {
      setUploadingFile(false)
    }
  }
  
  const handleFileDelete = async (fileId) => {
    try {
      await mdApi.deleteDischargeFile(patientId, fileId)
      // Reload the file list
      await loadDischargeFiles()
    } catch (error) {
      console.error('Error deleting file:', error)
      throw error // Re-throw so FileCard can display error
    }
  }
  
  const handleFileDownload = async (file) => {
    try {
      const blob = await mdApi.downloadDischargeFile(patientId, file.file_id)
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.filename
      document.body.appendChild(a)
      a.click()
      
      // Cleanup
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error downloading file:', error)
      alert('Failed to download file: ' + (error.message || 'Unknown error'))
    }
  }
  
  const loadNurseCallbacks = async () => {
    try {
      setLoadingCallbacks(true)
      const response = await mdApi.getNurseCallbacks({ patient_id: patientId })
      setNurseCallbacks(response.callbacks || [])
    } catch (error) {
      console.error('Error loading nurse callbacks:', error)
    } finally {
      setLoadingCallbacks(false)
    }
  }
  
  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true)
      setNotesError(null)
      setNotesSuccess(false)
      
      // Get the provider name (in a real app, this would come from auth context)
      const providerName = patient?.care_team?.primary_physician || 'Unknown Provider'
      
      await mdApi.saveDischargeNotes(patientId, dischargeNotes, providerName)
      
      setNotesSuccess(true)
      // Reload notes to get the saved version
      const notesData = await mdApi.getDischargeNotes(patientId)
      if (notesData.discharge_notes) {
        setExistingNotes(notesData.discharge_notes)
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setNotesSuccess(false), 3000)
    } catch (error) {
      console.error('Error saving notes:', error)
      setNotesError(error.message || 'Failed to save discharge notes')
    } finally {
      setSavingNotes(false)
    }
  }
  
  const handleCallPatient = async () => {
    try {
      setCallingPatient(true)
      setCallError(null)
      setCallSuccess(null)
      
      const response = await mdApi.initiateOutboundCall(patientId)
      
      setCallSuccess({
        callSid: response.call_sid,
        phoneNumber: response.phone_number,
        status: response.call_status
      })
    } catch (error) {
      console.error('Error initiating call:', error)
      setCallError(error.message || 'Failed to initiate call')
    } finally {
      setCallingPatient(false)
    }
  }
  
  const handleCompleteCallback = async (requestId) => {
    try {
      // In a real app, you'd have a modal for entering notes
      const providerName = patient?.care_team?.primary_physician || 'Unknown Provider'
      await mdApi.completeNurseCallback(requestId, providerName, 'Callback completed')
      
      // Reload callbacks
      loadNurseCallbacks()
    } catch (error) {
      console.error('Error completing callback:', error)
    }
  }
  
  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent': return '#EF4444'
      case 'high': return '#F59E0B'
      case 'routine': return '#3B82F6'
      default: return '#6B7280'
    }
  }
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock size={16} />
      case 'in_progress': return <User size={16} />
      case 'completed': return <Check size={16} />
      default: return <AlertCircle size={16} />
    }
  }
  
  // Handle patient field update (phone, email, care_team)
  const handlePatientUpdate = async (fieldName, value) => {
    try {
      const updates = { [fieldName]: value }
      const result = await mdApi.updatePatientDetail(patientId, updates)
      
      if (result.success) {
        // Reload patient data to get updated values
        const patientData = await mdApi.getPatientDetail(patientId)
        setPatient(patientData)
      }
    } catch (error) {
      console.error('Error updating patient:', error)
      throw error // Re-throw so the component can show the error
    }
  }
  
  // Handle care team update from modal
  const handleCareTeamUpdate = async (updates) => {
    try {
      const result = await mdApi.updatePatientDetail(patientId, updates)
      
      if (result.success) {
        // Reload patient data to get updated values
        const patientData = await mdApi.getPatientDetail(patientId)
        setPatient(patientData)
      }
    } catch (error) {
      console.error('Error updating care team:', error)
      throw error
    }
  }
  
  // Handle vaccination deletion
  const handleDeleteVaccination = async (vaccinationIndex) => {
    try {
      setDeletingVaccination(true)
      await mdApi.deletePatientVaccination(patientId, vaccinationIndex)
      
      // Reload patient data to show updated vaccinations
      const patientData = await mdApi.getPatientDetail(patientId)
      setPatient(patientData)
    } catch (error) {
      console.error('Error deleting vaccination:', error)
      alert('Failed to delete vaccination: ' + (error.message || 'Unknown error'))
    } finally {
      setDeletingVaccination(false)
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading patient details...</p>
      </div>
    )
  }

  if (!patient) {
    return (
      <div className="patient-detail-error">
        <p>Patient not found</p>
        <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
      </div>
    )
  }

  const riskColor = getRiskColor(patient.risk_assessment?.risk_level)

  return (
    <div className="patient-detail">
      <button className="back-button" onClick={() => navigate('/dashboard')}>
        <ArrowLeft size={20} />
        Back to Dashboard
      </button>

      <div className="patient-detail-header">
        <div className="patient-header-main">
          <div className="patient-header-info">
            <h1>{patient.full_name}</h1>
            <div className="patient-meta">
              <span>{patient.patient_id}</span>
              <span>•</span>
              <span>{patient.age} years old</span>
              <span>•</span>
              <span>{patient.dob}</span>
            </div>
            {/* Editable Contact Info Section */}
            <div className="patient-contact-info">
              <InlineEditField
                label="Phone"
                value={patient.phone}
                fieldName="phone"
                onSave={handlePatientUpdate}
                placeholder="No phone number"
                type="tel"
                validator={phoneValidator}
                formatDisplay={formatPhoneDisplay}
                icon={Phone}
              />
              <InlineEditField
                label="Email"
                value={patient.email}
                fieldName="email"
                onSave={handlePatientUpdate}
                placeholder="No email address"
                type="email"
                validator={emailValidator}
                icon={Mail}
              />
            </div>
          </div>
          <div className="patient-header-risk">
            <span className="risk-badge" style={{ backgroundColor: riskColor }}>
              {patient.risk_assessment?.risk_level?.toUpperCase()}
            </span>
            <span className="risk-score">Risk: {patient.risk_assessment?.overall_risk_score}/100</span>
          </div>
        </div>
        
        {/* Vitals Strip - Wearable data integrated into header */}
        {wearableData && (
          <div className="patient-vitals-strip">
            <div className="vitals-label">Current Vitals</div>
            <div className="vitals-metrics">
              <div className="vital-item vital-heart">
                <Heart size={16} />
                <span className="vital-value">{wearableData.heart_rate?.current}</span>
                <span className="vital-unit">bpm</span>
                <span className="vital-subtext">(rest: {wearableData.heart_rate?.resting})</span>
              </div>
              
              <div className="vital-divider" />
              
              <div className="vital-item vital-steps">
                <Activity size={16} />
                <span className="vital-value">{wearableData.activity?.steps_today?.toLocaleString()}</span>
                <span className="vital-unit">steps</span>
                <span className="vital-subtext">(avg: {wearableData.activity?.steps_7d_avg?.toLocaleString()})</span>
              </div>
              
              <div className="vital-divider" />
              
              <div className="vital-item vital-sleep">
                <Moon size={16} />
                <span className="vital-value">{wearableData.sleep?.last_night_hours}h</span>
                <span className="vital-unit">sleep</span>
                <span className="vital-subtext">(score: {wearableData.sleep?.sleep_score}/100)</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Active Concerns Panel - Priority position right after header */}
      <div className="concerns-priority-section">
        <ConcernsPanel
          patientId={patientId}
          onConcernResolved={(concernId, resolution) => {
            console.log('Concern resolved:', concernId, resolution)
            // Could trigger a refresh of patient data here
          }}
          onDiscussWithLuna={handleDiscussWithLuna}
        />
      </div>

      <div className="patient-detail-grid">
        {/* Appointments - Moved to top of grid */}
        {patient.appointments && (
          <div className="detail-card">
            <h2>Appointments</h2>
            <div className="appointments-section">
              <h3>Upcoming</h3>
              {patient.appointments.upcoming?.length > 0 ? (
                <ul>
                  {patient.appointments.upcoming.map((apt, idx) => (
                    <li key={idx}>
                      <strong>{new Date(apt.date).toLocaleDateString()}</strong> at {apt.time}
                      <br />
                      {apt.reason} with {apt.provider}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-secondary">No upcoming appointments</p>
              )}
            </div>
          </div>
        )}


        {/* Medical History - Tabbed Interface */}
        <div className="detail-card full-width">
          <MedicalHistoryTabs
            medicalHistory={patient.medical_history}
            consultationNotes={patient.consultation_notes}
            preventiveCare={patient.preventive_care}
            onAddVaccination={() => setShowVaccinationDialog(true)}
            onDeleteVaccination={handleDeleteVaccination}
          />
        </div>
        
        {/* Vaccination Upload Dialog */}
        <VaccinationUploadDialog
          isOpen={showVaccinationDialog}
          onClose={() => setShowVaccinationDialog(false)}
          patientId={patientId}
          patientName={patient.full_name}
          patientDob={patient.dob}
          existingVaccinations={patient.preventive_care?.vaccinations || []}
          onSuccess={async () => {
            // Reload patient data to show new vaccinations
            const patientData = await mdApi.getPatientDetail(patientId)
            setPatient(patientData)
            setShowVaccinationDialog(false)
          }}
        />

        {/* Care Team */}
        <div className="detail-card">
          <div className="care-team-header">
            <h2>Care Team</h2>
            <button
              className="btn-edit-care-team"
              onClick={() => setShowCareTeamModal(true)}
              title="Edit Care Team"
            >
              <Pencil size={14} />
              Edit
            </button>
          </div>
          <div className="care-team-list">
            {patient.care_team?.primary_physician ? (
              <p><strong>Primary Physician:</strong> {patient.care_team.primary_physician}</p>
            ) : (
              <p className="text-secondary"><strong>Primary Physician:</strong> Not assigned</p>
            )}
            {patient.care_team?.specialists?.length > 0 ? (
              <p><strong>Specialists:</strong> {patient.care_team.specialists.join(', ')}</p>
            ) : (
              <p className="text-secondary"><strong>Specialists:</strong> None assigned</p>
            )}
            {patient.care_team?.care_coordinator ? (
              <p><strong>Care Coordinator:</strong> {patient.care_team.care_coordinator}</p>
            ) : (
              <p className="text-secondary"><strong>Care Coordinator:</strong> Not assigned</p>
            )}
            {patient.care_team?.preferred_contact_method && (
              <p><strong>Preferred Contact:</strong> {patient.care_team.preferred_contact_method}</p>
            )}
          </div>
        </div>
        
        {/* Care Team Edit Modal */}
        <CareTeamEditModal
          isOpen={showCareTeamModal}
          onClose={() => setShowCareTeamModal(false)}
          careTeam={patient.care_team}
          onSave={handleCareTeamUpdate}
          patientName={patient.full_name}
        />

        {/* Caregivers (access grants) */}
        <CaregiversSection patientId={patientId} />

        {/* Nurse Callback Queue */}
        <div className="detail-card nurse-callback-card">
          <h2>
            <Phone size={20} />
            Nurse Callback Queue
          </h2>
          
          {loadingCallbacks ? (
            <div className="loading-callbacks">Loading callbacks...</div>
          ) : nurseCallbacks.length === 0 ? (
            <p className="text-secondary">No pending nurse callbacks for this patient.</p>
          ) : (
            <div className="callback-list">
              {nurseCallbacks.map((callback) => (
                <div key={callback.id} className={`callback-item urgency-${callback.urgency}`}>
                  <div className="callback-header">
                    <span
                      className="urgency-badge"
                      style={{ backgroundColor: getUrgencyColor(callback.urgency) }}
                    >
                      {callback.urgency?.toUpperCase()}
                    </span>
                    <span className="callback-status">
                      {getStatusIcon(callback.status)}
                      {callback.status}
                    </span>
                  </div>
                  
                  <div className="callback-details">
                    <p className="callback-reason"><strong>Reason:</strong> {callback.reason}</p>
                    {callback.question_topic && (
                      <p className="callback-topic"><strong>Topic:</strong> {callback.question_topic}</p>
                    )}
                    <p className="callback-time">
                      Requested: {new Date(callback.created_at).toLocaleString()}
                    </p>
                  </div>
                  
                  {callback.status === 'pending' && (
                    <button
                      className="btn-complete-callback"
                      onClick={() => handleCompleteCallback(callback.id)}
                    >
                      <Check size={14} />
                      Mark Complete
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <button
            className="btn-refresh-callbacks"
            onClick={loadNurseCallbacks}
            disabled={loadingCallbacks}
          >
            Refresh Callbacks
          </button>
        </div>

        {/* Conversation History */}
        <div className="detail-card full-width conversations-section-card">
          <ConversationsSection
            patientId={patientId}
            patientName={patient.full_name}
            patientInfo={{
              dob: patient.dob,
              mrn: patient.patient_id,
            }}
          />
        </div>

        {/* Discharge Planning Section - Moved to bottom */}
        <div className="detail-card full-width discharge-planning-card">
          <h2>
            <FileText size={20} />
            Discharge Planning
          </h2>
          
          {existingNotes && (
            <div className="existing-notes-info">
              <p className="notes-meta">
                Last updated: {new Date(existingNotes.created_at).toLocaleString()}
                by {existingNotes.created_by}
              </p>
              <p className="notes-status">
                Call Status: <span className={`call-status ${existingNotes.call_status}`}>
                  {existingNotes.call_status?.toUpperCase()}
                </span>
              </p>
            </div>
          )}
          
          <div className="discharge-notes-section">
            <label htmlFor="discharge-notes">Discharge Notes</label>
            <textarea
              id="discharge-notes"
              className="discharge-notes-textarea"
              placeholder="Enter discharge instructions, follow-up care requirements, medication changes, warning signs to watch for, and any other important patient instructions..."
              value={dischargeNotes}
              onChange={(e) => setDischargeNotes(e.target.value)}
              rows={8}
            />
          </div>
          
          {notesSuccess && (
            <div className="success-message">
              <Check size={16} />
              Discharge notes saved successfully!
            </div>
          )}
          
          {notesError && (
            <div className="error-message">
              <AlertCircle size={16} />
              {notesError}
            </div>
          )}
          
          {callSuccess && (
            <div className="success-message">
              <Phone size={16} />
              Call initiated! SID: {callSuccess.callSid}
            </div>
          )}
          
          {callError && (
            <div className="error-message">
              <AlertCircle size={16} />
              {callError}
            </div>
          )}
          
          <div className="discharge-actions">
            <button
              className="btn-save-notes"
              onClick={handleSaveNotes}
              disabled={savingNotes || !dischargeNotes.trim()}
            >
              {savingNotes ? 'Saving...' : 'Save Notes'}
            </button>
            
            <button
              className="btn-call-patient"
              onClick={handleCallPatient}
              disabled={callingPatient || !existingNotes}
              title={!existingNotes ? 'Save discharge notes first before calling patient' : `Call patient at ${patient.phone_number || 'unknown number'}`}
            >
              <Phone size={16} />
              {callingPatient
                ? 'Initiating Call...'
                : patient.phone_number
                  ? `Call Patient at ${patient.phone_number}`
                  : 'Call Patient'
              }
            </button>
          </div>
          
          {!existingNotes && (
            <p className="notes-hint">
              Save discharge notes before initiating a patient call.
            </p>
          )}
          
          {/* Discharge Files Section */}
          <div className="discharge-files-section">
            <div className="discharge-files-header">
              <h3>Attached Documents</h3>
              <div className="file-upload-controls">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
                  style={{ display: 'none' }}
                  id="discharge-file-input"
                />
                <button
                  className="btn-upload-file"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile}
                >
                  {uploadingFile ? (
                    <>
                      <Loader size={16} className="spinner" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Upload Files
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {uploadError && (
              <div className="error-message">
                <AlertCircle size={16} />
                {uploadError}
              </div>
            )}
            
            <div className="discharge-files-list">
              {loadingFiles ? (
                <div className="loading-files">
                  <Loader size={20} className="spinner" />
                  Loading files...
                </div>
              ) : dischargeFiles.length === 0 ? (
                <p className="no-files-message">
                  No documents attached yet. Upload files to include with discharge planning.
                </p>
              ) : (
                dischargeFiles.map((file) => (
                  <FileCard
                    key={file.file_id}
                    file={file}
                    patientId={patientId}
                    onDelete={handleFileDelete}
                    onDownload={handleFileDownload}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      
      {!activeLunaChat && patient && (
        <button
          type="button"
          className="luna-fab"
          onClick={openGeneralLuna}
          aria-label="Ask Luna Assistant"
          title="Ask Luna about this patient"
        >
          <MessageSquare size={24} />
        </button>
      )}

      {activeLunaChat && (
        <LunaTextPopup
          patientId={activeLunaChat.patientId}
          patientName={activeLunaChat.patientName}
          activeConcern={activeLunaChat.activeConcern}
          concernId={activeLunaChat.concernId}
          onClose={() => setActiveLunaChat(null)}
        />
      )}
    </div>
  )
}

export default PatientDetail